import { DayjsProperty } from '@/lib/jackson'
import { capitalizeFirstLetter } from '@/utils/helper'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Member } from '../member/model'
import { Fromable, GENDER, Role } from '../model'
import { Address, EmergencyContact, ReferralSource, ServiceData } from '../user/model'

export interface BranchAccessUserBranch {
  id: number
  name: string
}

export interface BranchAccessUser {
  id: number
  firstName: string
  lastName: string
  nickname: string
  email: string | null
  imageUrl: string | null
  code: string
  roleId: number
  roleName: string | null
  branchCount: number
  hasAllBranches: boolean
  branches: BranchAccessUserBranch[]
}

export interface BranchAuditEntry {
  id: number
  action: string
  branchName: string
  actorName: string | null
  createdAt: string
}

/**
 * QTY-2041: a staff record is never deleted — it is flipped to INACTIVE.
 * Records predating the feature have no value and are treated as ACTIVE.
 */
export type StaffAccountStatus = 'ACTIVE' | 'INACTIVE'

/** Filter value for the Staff Management list. `ALL` drops the filter entirely. */
export type StaffAccountStatusFilter = StaffAccountStatus | 'ALL'

/**
 * Records written before QTY-2041 carry no status and are working staff, so
 * only an explicit INACTIVE counts. Kept as a free function rather than a
 * getter on `Staff` — `Fromable.from` requires every non-method property, and
 * it also has to work on the plain rows `DataForTable()` produces.
 */
export const isStaffInactive = (status?: StaffAccountStatus | null): boolean =>
  status === 'INACTIVE'

/**
 * QTY-2041: one bucket of still-open work blocking a deactivation. The server
 * decides what counts (support tickets deliberately do not) — the client only
 * maps `type` to the menu the user has to visit to reassign it.
 */
export interface StaffPendingWorkItem {
  type: string
  count: number
  /** A few names to make the blocked dialog concrete; may be absent. */
  sampleNames?: string[]
}

/**
 * QTY-2043: one leg of the deactivate/reactivate timeline. A staff member can
 * leave and return more than once, so these accumulate rather than overwrite.
 */
@Serializable()
export class StaffStatusEvent extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('action')
  action!: 'DEACTIVATE' | 'REACTIVATE'

  @JsonProperty('actorName')
  actorName?: string | null

  @JsonProperty('reason')
  reason?: string | null

  @JsonProperty('effectiveDate')
  effectiveDate?: string | null

  /**
   * Nullable on purpose: `DayjsProperty` deserializes a missing, non-string or
   * unparseable value to `null`. Asserting non-null here would hide that from
   * the compiler and let a renamed field crash the render instead.
   */
  @DayjsProperty()
  createdAt!: Dayjs | null
}

@Serializable()
export class Staff extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('roleId')
  roleId!: number | string

  @JsonProperty({ path: 'role', type: Role })
  role!: Role

  @JsonProperty('presetCodes')
  presetCodes?: string[]

  @JsonProperty('code')
  code!: string

  @JsonProperty('citizenId')
  citizenId!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: GENDER

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('dateOfBirth')
  dateOfBirth!: string

  @JsonProperty('occupation')
  occupation!: string

  @JsonProperty('position')
  position!: string

  @JsonProperty('lineId')
  lineId!: string

  @JsonProperty('facebook')
  facebook!: string

  @JsonProperty('instagram')
  instagram!: string

  @JsonProperty('altPhone')
  altPhone!: string

  @JsonProperty('altEmail')
  altEmail!: string

  @JsonProperty({
    path: 'address',
    elementType: Address,
  })
  address!: Address[]

  @JsonProperty({
    path: 'emergencyContact',
    type: EmergencyContact,
  })
  emergencyContact!: EmergencyContact | null

  @JsonProperty({
    path: 'referralSource',
    type: ReferralSource,
  })
  referralSource!: ReferralSource

  @DayjsProperty()
  banUntil!: Dayjs

  /**
   * QTY-2041: soft-delete status. Absent on records written before the
   * feature shipped, so `isInactive` treats "missing" as ACTIVE.
   */
  @JsonProperty('accountStatus')
  accountStatus?: StaffAccountStatus

  @DayjsProperty()
  deactivatedAt?: Dayjs

  @JsonProperty('deactivationReason')
  deactivationReason?: string | null

  @JsonProperty('deactivationEffectiveDate')
  deactivationEffectiveDate?: string | null

  @DayjsProperty()
  reactivatedAt?: Dayjs

  public DataForTable() {
    const phoneNo =
      typeof this.phone === 'string'
        ? this.phone.startsWith('+66')
          ? this.phone.replace('+66', '0')
          : this.phone
        : '-'
    const nickname = this.nickname?.trim()
    const name = `${this.firstName} ${this.lastName}`
    return {
      id: this.id,
      code: this.code,
      name,
      // "First Last (nickname)" for the list/CSV; `name` stays the raw full name.
      displayName: nickname ? `${name} (${nickname})` : name,
      phone: phoneNo || '-',
      email: this.email || '-',
      role: this.role?.name ?? '',
      level: capitalizeFirstLetter(this.role?.position ?? '-'),
      roleId: this.roleId,
      presetCodes: this.presetCodes ?? [],
      // QTY-2041: drives the status chip column and which status action the
      // row menu offers. Defaults to ACTIVE so pre-feature rows render right.
      accountStatus: this.accountStatus ?? 'ACTIVE',
    }
  }

  public getOption() {
    return {
      value: this.id,
      label: `${this.firstName} ${this.lastName}`,
    }
  }

  public serializeForCreate() {
    return {
      roleId: this.roleId,
      citizenId: this.citizenId,
      firstName: this.firstName,
      lastName: this.lastName,
      nickname: this.nickname,
      gender: this.gender,
      dateOfBirth: this.dateOfBirth,
      email: this.email,
      phone: this.phone,
      position: this.position,
      address: this.address.map((addr: Address) => addr.serialize()),
      emergencyContact: this.emergencyContact ? this.emergencyContact.serialize() : null,
      referralSource: this.referralSource?.serialize(),
    }
  }
}

@Serializable()
export class WorkingDay extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('startTime')
  startTime?: string

  @JsonProperty('endTime')
  endTime?: string

  @JsonProperty('dayOfWeek')
  dayOfWeek!: string

  public serializeForCreate() {
    return {
      ...(this.id ? { id: this.id } : {}),
      dayOfWeek: this.dayOfWeek,
      startTime: this.startTime ?? '',
      endTime: this.endTime ?? '',
    }
  }
}

@Serializable()
export class TrainerClassHistory extends Fromable {
  @JsonProperty('scheduleId')
  scheduleId!: number

  @JsonProperty('classId')
  classId!: number

  @JsonProperty('className')
  className!: string

  @DayjsProperty()
  startTime!: Dayjs

  @DayjsProperty()
  endTime!: Dayjs

  @JsonProperty('status')
  status!: string

  @DayjsProperty()
  cancelledAt?: Dayjs

  @JsonProperty('branchId')
  branchId?: number

  @JsonProperty('branchName')
  branchName?: string

  @JsonProperty('location')
  location?: string

  @JsonProperty('roomId')
  roomId?: string | null
}

@Serializable()
export class TrainerSchedule extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('type')
  type!: string

  @DayjsProperty()
  startTime!: Dayjs

  @DayjsProperty()
  endTime!: Dayjs

  @JsonProperty('branchId')
  branchId?: number

  @JsonProperty('branchName')
  branchName?: string

  @JsonProperty('classId')
  classId?: number

  @JsonProperty('classTitle')
  classTitle?: string

  @JsonProperty('level')
  level?: string

  @JsonProperty('location')
  location?: string

  @JsonProperty('capacity')
  capacity?: number

  @DayjsProperty()
  cancelledAt?: Dayjs

  @JsonProperty('memberId')
  memberId?: number

  @JsonProperty({ path: 'member', type: Member, elementType: Member })
  member?: Member | Member[]

  @JsonProperty('memberJoin')
  memberJoin?: number

  @JsonProperty('description')
  description?: string

  @JsonProperty('status')
  status!: string

  // Reason a manager recorded when manually changing a PT booking's attendance
  // status (attended / not participating).
  @JsonProperty('statusNote')
  statusNote?: string

  @JsonProperty({ path: 'service', type: ServiceData })
  service?: ServiceData

  @JsonProperty('reason')
  reason?: string

  @JsonProperty('isPrivate')
  isPrivate?: boolean

  @JsonProperty('trainerId')
  trainerId?: number

  @JsonProperty({ path: 'trainer', type: Member })
  trainer?: Member
}

@Serializable()
export class TrainerPrivateTrainingHistory extends Fromable {
  @JsonProperty('bookingId')
  bookingId!: number

  @DayjsProperty()
  startTime!: Dayjs

  @DayjsProperty()
  endTime!: Dayjs

  @JsonProperty('status')
  status!: string

  @JsonProperty('memberId')
  memberId?: number

  @JsonProperty('memberFirstName')
  memberFirstName?: string

  @JsonProperty('memberLastName')
  memberLastName?: string

  @JsonProperty('memberNickname')
  memberNickname?: string

  @JsonProperty('memberPhone')
  memberPhone?: string
}

@Serializable()
export class TrainerGoalSetting extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('memberNickname')
  memberNickname?: string

  @JsonProperty('memberPhone')
  memberPhone?: string

  @JsonProperty('goals')
  goals!: string[]

  @JsonProperty('status')
  status!: string

  @JsonProperty('startDate')
  startDate!: string

  @JsonProperty('endDate')
  endDate!: string

  @JsonProperty('summary')
  summary?: string

  @JsonProperty('detail')
  detail?: string

  @DayjsProperty()
  createdAt!: Dayjs
}

/** A piece of an activity log's detail; `highlight` = a person's name. */
export interface StaffActivityLogDetailSegment {
  text: string
  highlight: boolean
}

@Serializable()
export class StaffActivityLog extends Fromable {
  @JsonProperty()
  id!: string

  @JsonProperty()
  staffId!: number

  @JsonProperty()
  eventType?: string

  @JsonProperty()
  detail!: string

  /** `detail` in pieces with the people's names flagged, shown in bold (B-916). */
  @JsonProperty()
  detailSegments?: StaffActivityLogDetailSegment[]

  @JsonProperty()
  createdAt!: string
}
